/**
 * coingecko-api.js
 *
 * CoinGecko is now used only for:
 *   - fetchTopCoins()  — market cap rankings + coin metadata (name, image, market_cap)
 *   - fetchTrending()  — trending coins for the right panel
 *
 * Historical OHLCV data and live prices have moved to binance-api.js,
 * which has no rate limits and returns true OHLCV candles.
 */

// ─── Imports ──────────────────────────────────────────────────────────────────
import { getCoinGeckoProxyBase, API_MODE } from './api-config';

// ─── Cache ────────────────────────────────────────────────────────────────────
const cache = new Map();

const CACHE_TTL = {
  markets:     60 * 1000,        // 1 minute — market cap rankings
  trending:    10 * 60 * 1000,   // 10 minutes — trending list changes slowly
  stablecoins: 60 * 60 * 1000,  // 1 hour — stablecoin category rarely changes
};

// ─── Rate limiting (kept for the two remaining CoinGecko calls) ───────────────
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  retryDelay: 2000, // Base delay; doubles on each retry (exponential backoff)
};

function isCacheValid(entry, ttl) {
  return entry && (Date.now() - entry.timestamp) < ttl;
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function apiCall(endpoint, queryParams = {}, retryCount = 0) {
  const baseUrl = getCoinGeckoProxyBase();
  let url;
  if (API_MODE === 'vps' || baseUrl.startsWith('https://api.coingecko.com')) {
    // VPS or direct: baseUrl/endpoint?query
    const params = new URLSearchParams(queryParams);
    url = `${baseUrl}${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
  } else {
    // Netlify: path is a query param
    const params = new URLSearchParams({ path: endpoint, ...queryParams });
    url = `${baseUrl}?${params.toString()}`;
  }

  console.log(`[CoinGecko] ${endpoint}`);

  const response = await fetch(url);

  if (response.status === 429) {
    if (retryCount < RATE_LIMIT_CONFIG.maxRetries) {
      const wait = RATE_LIMIT_CONFIG.retryDelay * Math.pow(2, retryCount);
      console.warn(`[CoinGecko] Rate limited. Retrying in ${wait}ms (attempt ${retryCount + 1})`);
      await delay(wait);
      return apiCall(endpoint, queryParams, retryCount + 1);
    }
    throw new Error(`CoinGecko rate limit exceeded after ${RATE_LIMIT_CONFIG.maxRetries} retries`);
  }

  if (!response.ok) {
    throw new Error(`CoinGecko API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch top cryptocurrencies by market cap.
 * Used once on app load for metadata: name, symbol, image, market_cap, market_cap_rank.
 * Live prices are subsequently overwritten by Binance ticker data.
 *
 * @param {number} limit  Number of coins to fetch (default 50)
 * @returns {Promise<Array>}
 */
export async function fetchTopCoins(limit = 50) {
  const cacheKey = `markets_${limit}`;
  const cached = cache.get(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.markets)) {
    console.log('[CoinGecko] Using cached market data');
    return cached.data;
  }

  try {
    const data = await apiCall('/coins/markets', {
      vs_currency:          'usd',
      order:                'market_cap_desc',
      per_page:             limit.toString(),
      page:                 '1',
      sparkline:            'false',
      price_change_percentage: '24h',
    });

    cache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('[CoinGecko] Failed to fetch top coins:', error);
    // Return stale cache if available so the UI still has something to show
    if (cached?.data?.length) return cached.data;
    // Re-throw so the caller can show the real error instead of "No market data received"
    throw error;
  }
}

/**
 * Fetch the set of CoinGecko IDs in the "stablecoins" category.
 * Used to filter stablecoins from the dashboard using CoinGecko's own classification
 * rather than a hardcoded list. Cached for 1 hour.
 *
 * @returns {Promise<Set<string>|null>}  Set of stablecoin IDs, or null on failure (caller should fall back)
 */
export async function fetchStablecoinIds() {
  const cacheKey = 'stablecoin_ids';
  const cached = cache.get(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.stablecoins)) {
    return cached.data;
  }

  try {
    const data = await apiCall('/coins/markets', {
      vs_currency: 'usd',
      category:    'stablecoins',
      per_page:    '250',
      page:        '1',
    });
    const ids = new Set(data.map(c => c.id));
    cache.set(cacheKey, { data: ids, timestamp: Date.now() });
    console.log(`[CoinGecko] Stablecoin category loaded: ${ids.size} coins`);
    return ids;
  } catch (error) {
    console.warn('[CoinGecko] Failed to fetch stablecoin category, will use fallback list:', error.message);
    return cached?.data || null;
  }
}

/**
 * Fetch trending cryptocurrencies for the right-panel insights section.
 *
 * @returns {Promise<Object>}  { coins: [...] }
 */
export async function fetchTrending() {
  const cacheKey = 'trending';
  const cached = cache.get(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.trending)) {
    console.log('[CoinGecko] Using cached trending data');
    return cached.data;
  }

  try {
    const data = await apiCall('/search/trending');
    cache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('[CoinGecko] Failed to fetch trending:', error);
    return cached?.data || { coins: [] };
  }
}

/**
 * Fetch 30-day price history for a coin.
 * Used as a sparkline fallback for coins not listed on Binance.
 * Returns [[timestamp, price], ...] — same shape as parseBinanceKlinesForSparkline.
 *
 * @param {string} coinId  CoinGecko ID, e.g. "nexo"
 * @returns {Promise<Array>}  [[timestamp, price], ...]
 */
export async function fetchCoinPriceHistory(coinId) {
  const cacheKey = `chart_${coinId}`;
  const cached = cache.get(cacheKey);
  const CHART_TTL = 10 * 60 * 1000; // 10 minutes

  if (cached && Date.now() - cached.timestamp < CHART_TTL) {
    return cached.data;
  }

  try {
    const data = await apiCall(`/coins/${coinId}/market_chart`, {
      vs_currency: 'usd',
      days: '30',
    });
    const prices = data?.prices || [];
    cache.set(cacheKey, { data: prices, timestamp: Date.now() });
    return prices;
  } catch (error) {
    console.warn(`[CoinGecko] Price history unavailable for ${coinId}:`, error.message);
    return cached?.data || [];
  }
}

// ─── Cache utilities ──────────────────────────────────────────────────────────

/** Clear all CoinGecko cache entries. */
export function clearCache() {
  cache.clear();
  console.log('[CoinGecko] Cache cleared');
}

/** Return cache status summary for the Settings page. */
export function getCacheStatus() {
  const status = {};
  cache.forEach((value, key) => {
    const age = Date.now() - value.timestamp;
    const ttl = key === 'trending' ? CACHE_TTL.trending : CACHE_TTL.markets;
    status[key] = {
      age:   Math.round(age / 1000) + 's',
      valid: age < ttl,
    };
  });
  return status;
}
